"use client";

import { ChevronDown } from "lucide-react";
import { useParams, useSearchParams } from "next/navigation";
import { useTranslations } from "next-intl";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { locales } from "@/config";
import { Link, usePathname } from "@/i18n/navigation";
import { cn } from "@/lib/utils";
const localeLabels: Record<(typeof locales)[number], string> = {
  da: "🇩🇰 DA",
  de: "🇩🇪 DE",
  en: "🇺🇸 EN",
  es: "🇪🇸 ES",
  fr: "🇫🇷 FR",
  it: "🇮🇹 IT",
  pt: "🇵🇹 PT",
  el: "🇬🇷 EL",
  nl: "🇳🇱 NL",
  fi: "🇫🇮 FI",
  is: "🇮🇸 IS",
  fo: "🇫🇴 FO",
  no: "🇳🇴 NO",
  sv: "🇸🇪 SV"
};
export const localeFlags: Record<(typeof locales)[number], string> = {
  da: "🇩🇰",
  de: "🇩🇪",
  en: "🇺🇸",
  es: "🇪🇸",
  fr: "🇫🇷",
  it: "🇮🇹",
  pt: "🇵🇹",
  el: "🇬🇷",
  nl: "🇳🇱",
  fi: "🇫🇮",
  is: "🇮🇸",
  fo: "🇫🇴",
  no: "🇳🇴",
  sv: "🇸🇪"
};
interface RouteLanguageSwitcherProps {
  align?: "start" | "center" | "end";
  triggerClassName?: string;
  labelMode?: "full" | "flag" | "responsive";
}
export function RouteLanguageSwitcher({
  align = "end",
  triggerClassName,
  labelMode = "full"
}: RouteLanguageSwitcherProps) {
  const t = useTranslations("MainNav");
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const params = useParams();
  const currentLocale = typeof params?.locale === "string" && locales.includes(params.locale as (typeof locales)[number]) ? params.locale as (typeof locales)[number] : "en";
  const routeParams = Object.fromEntries(Object.entries(params ?? {}).filter(([key]) => key !== "locale"));
  const query = Object.fromEntries(searchParams.entries());
  const localizedHref = {
    pathname,
    params: routeParams,
    query
  } as Parameters<typeof Link>[0]["href"];
  const persistLocale = (nextLocale: (typeof locales)[number]) => {
    document.cookie = `NEXT_LOCALE=${nextLocale}; path=/; max-age=31536000; samesite=lax`;
  };
  return <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button type="button" className={cn("inline-flex items-center gap-1 rounded-md border border-border bg-background px-3 py-2 text-sm font-medium shadow-sm transition-colors hover:bg-accent hover:text-accent-foreground", triggerClassName)}>
          {labelMode === "responsive" ? <>
              <span className="md:hidden">{localeFlags[currentLocale]}</span>
              <span className="hidden md:inline">{localeLabels[currentLocale]}</span>
            </> : <span>{labelMode === "flag" ? localeFlags[currentLocale] : localeLabels[currentLocale]}</span>}
          <ChevronDown className="h-4 w-4" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align={align}>
        <DropdownMenuLabel>{t("changeLanguage")}</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {locales.map(locale => <DropdownMenuItem asChild key={locale}>
            <Link href={localizedHref} locale={locale} className="cursor-pointer" onClick={() => persistLocale(locale)}>
              {localeLabels[locale]}
            </Link>
          </DropdownMenuItem>)}
      </DropdownMenuContent>
    </DropdownMenu>;
}